import React from 'react';

import { Field } from '../../../design-system/components/desktop/primitives';
import { useViewport } from '../../../design-system/hooks/useViewport';
import { TrackButton, TrackDropzone, TrackInput, TrackTextarea } from './tracking.parts';

/**
 * The dispute form body. Pure: every input is a prop, every output a callback.
 *
 * FIXED HERE, RECORDED IN `CONVERSION-MAP.md` UNDER "Known app bugs"
 * ------------------------------------------------------------------
 * The dispute REASON input used to carry both `placeholder="1234-567-91011"`
 * and `defaultValue="1234-567-91011"`, copy-pasted wholesale from the tracking
 * ID field two components over. So every dispute opened with a tracking number
 * pre-typed into the reason box, and any buyer who did not clear it filed
 * "1234-567-91011" as their complaint. The field is now a controlled input with
 * a reason-shaped placeholder and no default value at all.
 *
 * Two further things went with it:
 *   - The Skip button was `type="submit"`, so "Skip" filed the dispute. It is a
 *     real cancel now.
 *   - The form's `onSubmit` was `()=>onSubmit(reason)`, which called the handler
 *     during render rather than on submit and never called `preventDefault`, so
 *     the page reloaded. Both fixed.
 *
 * A dispute is the exception path, so the ink command is "Submit dispute" and
 * "Skip" is a ghost. There is no `danger` variant: `.dsk-btn--danger` does not
 * exist, and a destructive command is a ghost button plus a confirmation.
 */
export function DisputeForm({
  reason = '',
  onReasonChange = () => {},
  description = '',
  onDescriptionChange = () => {},
  evidencePreview = null,
  onEvidenceChange = () => {},
  onSubmit = () => {},
  onSkip,
  loading = false,
}) {
  const { isDesktop } = useViewport();

  function handleSubmit(event) {
    event.preventDefault();
    onSubmit({ reason, description });
  }

  const reasonInput = (
    <TrackInput
      type="text"
      name="disputeReason"
      value={reason}
      onChange={(event) => onReasonChange(event.target.value)}
      placeholder="Condition does not match the listing"
      autoComplete="off"
      disabled={loading}
      aria-label="Dispute reason"
    />
  );

  const descriptionInput = (
    <TrackTextarea
      rows={5}
      name="disputeDescription"
      value={description}
      onChange={(event) => onDescriptionChange(event.target.value)}
      placeholder="Tell us what is wrong, in as much detail as you can."
      disabled={loading}
      aria-label="Dispute description"
    />
  );

  const evidence = (
    <TrackDropzone id="disputeEvidence" preview={evidencePreview} onChange={onEvidenceChange} />
  );

  return (
    <form className="dsa-track__form" onSubmit={handleSubmit} noValidate>
      {isDesktop ? (
        <>
          <Field label="Dispute reason">{reasonInput}</Field>
          <Field label="Description">{descriptionInput}</Field>
          <Field label="Evidence" optional>
            {evidence}
          </Field>
        </>
      ) : (
        <>
          <label className="ds-field">
            <span className="ds-field__label">Dispute reason</span>
            <span className="ds-field__control">{reasonInput}</span>
          </label>
          <label className="ds-field">
            <span className="ds-field__label">Description</span>
            <span className="ds-field__control">{descriptionInput}</span>
          </label>
          <div className="ds-field">
            <span className="ds-field__label">Evidence</span>
            {evidence}
          </div>
        </>
      )}

      <div className="dsa-track__form-actions">
        {onSkip ? (
          <TrackButton variant="ghost" onClick={onSkip} disabled={loading}>
            Skip
          </TrackButton>
        ) : null}
        <TrackButton type="submit" loading={loading} disabled={loading || !reason.trim()}>
          {loading ? 'Submitting' : 'Submit dispute'}
        </TrackButton>
      </div>
    </form>
  );
}

export default DisputeForm;
